import { useState, useEffect, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Landmark, Check, Plus, FileText, Search, ShieldCheck, Copy, Link2, Unlink, BadgeCheck, PiggyBank, UploadCloud, TrendingUp, TrendingDown, History, Minus } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function DiscoveryView({ onChanged }) {
  const [scanning, setScanning] = useState({});
  const [results, setResults] = useState({});
  const [summaries, setSummaries] = useState({});
  const [modes, setModes] = useState({});
  const [importing, setImporting] = useState({});
  const [imported, setImported] = useState({});
  const [gmail, setGmail] = useState({ configured: false, connected: false, email: null, loaded: false });
  const [uploading, setUploading] = useState(false);
  const [evolution, setEvolution] = useState(null);
  const stmtRef = useRef(null);

  const loadGmailStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/oauth/gmail/status");
      setGmail({ ...data, loaded: true });
    } catch {
      setGmail((g) => ({ ...g, loaded: true }));
    }
  }, []);

  const loadEvolution = useCallback(async () => {
    try {
      const { data } = await api.get("/statements/evolution");
      setEvolution(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadGmailStatus();
    loadEvolution();
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("gmail");
    if (flag === "connected") {
      toast.success("Gmail connecté — lancez le scan de votre boîte");
      loadGmailStatus();
    } else if (flag === "error") {
      toast.error("La connexion Gmail a échoué. Réessayez.");
    }
    if (flag) window.history.replaceState({}, "", window.location.pathname);
  }, [loadGmailStatus, loadEvolution]);

  const connectGmail = () => {
    window.location.href = `${BACKEND}/api/oauth/gmail/login`;
  };

  const disconnectGmail = async () => {
    try {
      await api.post("/oauth/gmail/disconnect");
      setGmail((g) => ({ ...g, connected: false, email: null }));
      setResults((r) => ({ ...r, gmail: null }));
      toast.success("Compte Gmail déconnecté");
    } catch {
      toast.error("Impossible de déconnecter Gmail");
    }
  };

  const onStatementFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    setResults((r) => ({ ...r, bank: null }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/discovery/statement", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResults((r) => ({ ...r, bank: data.items }));
      setSummaries((sm) => ({ ...sm, bank: data.summary }));
      setModes((m) => ({ ...m, bank: data.mode }));
      const seed = {};
      data.items.forEach((it) => { if (it.already_imported) seed[it.id] = true; });
      setImported((im) => ({ ...im, ...seed }));
      toast.success(`${data.count} prélèvement(s) d'assurance détecté(s)`);
      loadEvolution();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Échec de l'analyse du relevé");
    } finally {
      setUploading(false);
    }
  };

  const scan = async (source) => {
    setScanning((s) => ({ ...s, [source]: true }));
    setResults((r) => ({ ...r, [source]: null }));
    try {
      const { data } = await api.post("/discovery/scan", { source });
      setResults((r) => ({ ...r, [source]: data.items }));
      setSummaries((sm) => ({ ...sm, [source]: data.summary }));
      setModes((m) => ({ ...m, [source]: data.mode }));
      const seed = {};
      data.items.forEach((it) => { if (it.already_imported) seed[it.id] = true; });
      setImported((im) => ({ ...im, ...seed }));
      toast.success(`${data.count} contrat(s) détecté(s)`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec du scan");
      if (source === "gmail") loadGmailStatus();
    } finally {
      setScanning((s) => ({ ...s, [source]: false }));
    }
  };

  const importItem = async (source, item) => {
    setImporting((i) => ({ ...i, [item.id]: true }));
    try {
      const { data } = await api.post("/discovery/import", { source, item });
      setImported((im) => ({ ...im, [item.id]: true }));
      if (data.duplicate_of) {
        toast.warning(`« ${item.name} » importé — doublon potentiel avec « ${data.duplicate_of} »`);
      } else {
        toast.success(`« ${item.name} » importé et analysé`);
      }
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Import impossible");
    } finally {
      setImporting((i) => ({ ...i, [item.id]: false }));
    }
  };

  const importAll = async (source) => {
    const items = (results[source] || []).filter((it) => !imported[it.id]);
    for (const it of items) {
      await importItem(source, it); // eslint-disable-line no-await-in-loop
    }
  };

  const gmailRealActive = gmail.configured && gmail.connected;

  const renderList = (key) => {
    const items = results[key];
    if (!items) return null;
    const pending = items.filter((it) => !imported[it.id]).length;
    const isReal = modes[key] === "real";
    const sm = summaries[key];
    // Priorité : les doublons non encore importés remontent en tête de liste.
    const sorted = [...items].sort(
      (a, b) => (b.duplicate_of && !imported[b.id] ? 1 : 0) - (a.duplicate_of && !imported[a.id] ? 1 : 0)
    );
    return (
      <div className="mt-5 space-y-3">
        {sm && (
          <div data-testid={`scan-summary-${key}`} className="rounded-xl bg-muted/60 border border-border p-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
            <span><span className="font-heading font-bold">{sm.found}</span> trouvé(s)</span>
            <span className="text-muted-foreground">·</span>
            <span><span className="font-heading font-bold">{Math.round(sm.monthly_total)} €</span>/mois</span>
            <span className="text-muted-foreground">·</span>
            <span className={`flex items-center gap-1 font-medium ${sm.potential_duplicates > 0 ? "text-ochre" : "text-muted-foreground"}`}>
              <Copy className="w-3.5 h-3.5" />{sm.potential_duplicates} doublon(s)
            </span>
            {sm.potential_savings > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span data-testid={`scan-savings-${key}`} className="flex items-center gap-1 font-semibold text-emerald-600">
                  <PiggyBank className="w-3.5 h-3.5" />≈ {Math.round(sm.potential_savings)} €/an économisables
                </span>
              </>
            )}
            {sm.increases > 0 && (
              <>
                <span className="text-muted-foreground">·</span>
                <span data-testid={`scan-increases-${key}`} className="flex items-center gap-1 font-semibold text-destructive">
                  <TrendingUp className="w-3.5 h-3.5" />{sm.increases} hausse(s) vs dernier relevé
                </span>
              </>
            )}
            {isReal && (
              <span data-testid="gmail-real-badge" className="ml-auto flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600">
                <BadgeCheck className="w-3.5 h-3.5" /> Boîte réelle
              </span>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <p className="text-xs font-mono uppercase tracking-wide text-muted-foreground">{items.length} détecté(s)</p>
          {pending > 0 && (
            <button data-testid={`import-all-${key}`} onClick={() => importAll(key)} className="text-xs font-medium text-primary hover:underline">
              Tout importer ({pending})
            </button>
          )}
        </div>
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border p-4 text-center">
            Aucun contrat d'assurance détecté dans votre boîte.
          </p>
        )}
        {sorted.map((it) => {
          const done = imported[it.id];
          const isDup = it.duplicate_of && !done;
          return (
            <div key={it.id} data-testid={`finding-${it.id}`} className={`rounded-xl border p-4 transition ${isDup ? "border-ochre/50 bg-ochre/5 ring-1 ring-ochre/20" : "border-border"}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${isDup ? "bg-ochre/15 text-ochre" : "bg-muted text-muted-foreground"}`}>
                  {isDup ? <Copy className="w-4 h-4" /> : key === "gmail" ? <FileText className="w-4 h-4" /> : <Landmark className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{it.name}</p>
                    {isDup && (
                      <span className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-ochre text-white">Prioritaire</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {key === "gmail" ? `${it.from || ""}${it.subject ? ` · ${it.subject}` : ""}` : `${it.label} · ${it.amount} €/mois`}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-accent text-accent-foreground">{it.insurer}</span>
                    <span className="text-[10px] font-mono text-muted-foreground">{it.category}</span>
                    {isDup && (
                      <span data-testid={`dup-badge-${it.id}`} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-ochre/15 text-ochre flex items-center gap-1">
                        <Copy className="w-3 h-3" /> Doublon avec « {it.duplicate_of} »
                      </span>
                    )}
                    {typeof it.delta === "number" && Math.abs(it.delta) >= 0.01 && (
                      <span data-testid={`delta-badge-${it.id}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${it.delta > 0 ? "bg-destructive/10 text-destructive" : "bg-emerald-500/10 text-emerald-600"}`}>
                        {it.delta > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {it.delta > 0 ? "+" : ""}{it.delta} €/mois vs dernier relevé
                      </span>
                    )}
                  </div>
                </div>
                {done ? (
                  <span className="flex items-center gap-1 text-xs font-medium text-emerald-600 shrink-0">
                    <Check className="w-4 h-4" /> Importé
                  </span>
                ) : (
                  <button
                    data-testid={`import-${it.id}`}
                    onClick={() => importItem(key, it)}
                    disabled={importing[it.id]}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 disabled:opacity-60 transition shrink-0"
                  >
                    {importing[it.id] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Importer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-8 animate-in-up" data-testid="discovery-view">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Recherche auto</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Retrouvez vos contrats automatiquement</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Connectez votre boîte mail et votre banque : Iacover repère vos assurances, importe les contrats et les analyse pour vous. Fini la saisie manuelle.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {/* Gmail */}
        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
              <Mail className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold">Boîte mail (Gmail)</p>
              <p className="text-sm text-muted-foreground">Emails & pièces jointes d'assurance</p>
            </div>
            {gmailRealActive && (
              <span data-testid="gmail-connected-badge" className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-600 shrink-0">
                <BadgeCheck className="w-3.5 h-3.5" /> Connecté
              </span>
            )}
          </div>

          {gmailRealActive && gmail.email && (
            <p className="text-xs text-muted-foreground mt-3 truncate">Compte : <span className="font-medium text-foreground">{gmail.email}</span></p>
          )}

          {gmail.configured && !gmail.connected ? (
            <button
              data-testid="gmail-connect-btn"
              onClick={connectGmail}
              className="mt-5 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition flex items-center justify-center gap-2"
            >
              <Link2 className="w-4 h-4" /> Connecter Gmail & analyser
            </button>
          ) : (
            <button
              data-testid="scan-gmail-btn"
              onClick={() => scan("gmail")}
              disabled={scanning.gmail}
              className="mt-5 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60 transition flex items-center justify-center gap-2"
            >
              {scanning.gmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {scanning.gmail ? "Analyse en cours…" : results.gmail ? "Relancer le scan" : gmailRealActive ? "Scanner ma boîte Gmail" : "Connecter Gmail & analyser"}
            </button>
          )}

          <div className="flex items-center justify-center gap-2 mt-2">
            <p className="text-[11px] font-mono text-muted-foreground text-center">
              {gmailRealActive ? "Connexion Google active" : gmail.configured ? "Connexion Google sécurisée" : "Connexion Google sécurisée (démo)"}
            </p>
            {gmailRealActive && (
              <button data-testid="gmail-disconnect-btn" onClick={disconnectGmail} className="text-[11px] font-medium text-muted-foreground hover:text-destructive transition flex items-center gap-1">
                <Unlink className="w-3 h-3" /> Déconnecter
              </button>
            )}
          </div>

          {renderList("gmail")}
        </div>

        {/* Relevé bancaire */}
        <div className="rounded-2xl border border-border bg-card p-6 flex flex-col">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
              <Landmark className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-heading font-bold">Relevé bancaire</p>
              <p className="text-sm text-muted-foreground">Importez un relevé (PDF/CSV), l'IA détecte vos prélèvements d'assurance</p>
            </div>
          </div>
          <input ref={stmtRef} type="file" accept=".pdf,.csv,.txt" className="hidden" onChange={onStatementFile} data-testid="statement-file-input" />
          <button
            data-testid="import-statement-btn"
            onClick={() => stmtRef.current?.click()}
            disabled={uploading}
            className="mt-5 h-11 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-60 transition flex items-center justify-center gap-2"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {uploading ? "Analyse du relevé…" : "Importer mon relevé (PDF/CSV)"}
          </button>
          <button
            data-testid="scan-bank-btn"
            onClick={() => scan("bank")}
            disabled={scanning.bank}
            className="mt-2 h-9 text-xs font-medium text-muted-foreground hover:text-foreground transition flex items-center justify-center gap-1.5 disabled:opacity-60"
          >
            {scanning.bank ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {scanning.bank ? "Analyse en cours…" : "Voir une démonstration"}
          </button>

          {renderList("bank")}
        </div>
      </div>

      {evolution && evolution.snapshots > 0 && (
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="statement-history">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center"><History className="w-4 h-4" /></div>
            <div>
              <p className="font-heading font-bold">Historique & évolution des prélèvements</p>
              <p className="text-xs text-muted-foreground">{evolution.snapshots} relevé(s) importé(s) · comparaison automatique mois par mois</p>
            </div>
          </div>
          <div className="space-y-2">
            {evolution.contracts.map((c, i) => (
              <div key={i} data-testid={`evolution-row-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{c.insurer} · {c.category}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-sm font-heading font-bold">{c.latest} €/mois</span>
                  {typeof c.delta === "number" ? (
                    <span className={`text-xs font-semibold flex items-center gap-1 ${c.delta > 0 ? "text-destructive" : c.delta < 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {c.delta > 0 ? <TrendingUp className="w-3.5 h-3.5" /> : c.delta < 0 ? <TrendingDown className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                      {c.delta > 0 ? "+" : ""}{c.delta} €
                    </span>
                  ) : (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">nouveau</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-dashed border-border p-5 flex items-start gap-3 text-sm text-muted-foreground">
        <ShieldCheck className="w-5 h-5 shrink-0 text-primary mt-0.5" />
        {gmailRealActive ? (
          <p><span className="font-medium text-foreground">Gmail connecté.</span> Le scan lit vos vrais emails et pièces jointes d'assurance (accès en lecture seule) puis l'IA en extrait vos contrats. L'agrégation bancaire reste en mode démonstration.</p>
        ) : (
          <p><span className="font-medium text-foreground">Mode démonstration.</span> {gmail.configured ? "Connectez votre compte Google pour scanner votre vraie boîte Gmail." : "Les emails et prélèvements affichés sont simulés pour valider le parcours. La connexion Gmail réelle (OAuth Google) et l'agrégation bancaire via un prestataire régulé seront activées avec vos accès."}</p>
        )}
      </div>
    </div>
  );
}
